import { Link } from 'react-router-dom';
import { ArrowRight, Factory, FlaskConical, Cog, Shield, TrendingUp, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_spirits-consulting/artifacts/xwh8nobz_IMG-20260614-WA0028.jpg";

export const Home = () => {
  const highlights = [
    { number: '37+', label: 'Years Experience' },
    { number: '100+', label: 'Projects Delivered' },
    { number: '50+', label: 'Client Organizations' },
  ];

  const services = [
    {
      icon: Factory,
      title: 'Manufacturing Excellence',
      description: 'End-to-end advisory for distillation, blending, bottling, and packaging operations.',
    },
    {
      icon: FlaskConical,
      title: 'Distillery Operations',
      description: 'Expert guidance on grain spirit, malt spirit, grape spirit, and wine making projects.',
    },
    {
      icon: Cog,
      title: 'Operational Transformation',
      description: 'Productivity improvement, cost optimization, and OEE enhancement programs.',
    },
    {
      icon: Shield,
      title: 'Compliance & Governance',
      description: 'Regulatory compliance advisory and excise coordination for AlcoBev operations.',
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1625264412785-7c94942908a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHx3aGlza2V5JTIwYmFycmVscyUyMGNlbGxhciUyMGFtYmVyJTIwc3Bpcml0c3xlbnwwfHx8fDE3ODE0NDI2MzZ8MA&ixlib=rb-4.1.0&q=85"
            alt="Whiskey Barrels"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Blue glow effect */}
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-500/20 rounded-full">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                <span className="text-blue-400 text-sm font-medium tracking-wide uppercase">AlcoBev Industry Experts</span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight tracking-tight">
                Future-Ready<br />
                <span className="text-blue-400">Manufacturing</span><br />
                Solutions
              </h1>
              
              <p className="text-lg lg:text-xl text-slate-300 leading-relaxed max-w-xl">
                End-to-end advisory and execution support for Alcoholic Beverages 
                and FMCG organizations. Building efficient, scalable, and sustainable operations.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/services">
                  <Button 
                    data-testid="hero-cta-services"
                    className="bg-blue-600 text-white hover:bg-blue-700 rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider btn-shadow w-full sm:w-auto"
                  >
                    Explore Services
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    data-testid="hero-cta-about"
                    variant="outline"
                    className="border-slate-600 text-white hover:bg-slate-800 rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider w-full sm:w-auto"
                  >
                    About the Founder
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Highlights */}
            <div className="hidden lg:flex flex-col gap-6">
              {highlights.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 hover:bg-white/10 transition-all duration-300"
                >
                  <p className="text-5xl font-serif font-bold text-blue-400 mb-2">{item.number}</p>
                  <p className="text-slate-400 uppercase tracking-wider text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Mobile Highlights */}
          <div className="grid grid-cols-3 gap-4 mt-12 lg:hidden">
            {highlights.map((item, index) => (
              <div key={index} className="text-center">
                <p className="text-2xl sm:text-3xl font-serif font-bold text-blue-400">{item.number}</p>
                <p className="text-slate-400 text-xs uppercase tracking-wider mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section data-testid="services-preview" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">What We Offer</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
              Comprehensive AlcoBev<br />Consulting Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="service-card bg-slate-50 p-8 lg:p-10 border border-slate-100 hover:border-blue-100 group"
              >
                <div className="w-14 h-14 bg-blue-600/10 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl lg:text-2xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="services-view-all"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider btn-shadow"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Teaser Section */}
      <section data-testid="founder-teaser" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-blue-600/10"></div>
              <div className="relative img-zoom">
                <img 
                  src={FOUNDER_IMAGE}
                  alt="V Raghunath - Founder"
                  className="w-full aspect-[4/5] object-cover object-top"
                />
              </div>
            </div>
            
            <div className="space-y-6">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Meet the Expert</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
                V Raghunath
              </h2>
              <p className="text-xl text-slate-600">
                Founder & Principal Consultant
              </p>
              <div className="border-l-4 border-blue-600 pl-6 py-2">
                <p className="text-slate-700 leading-relaxed text-lg italic">
                  "Over 37 years of experience in leading manufacturing and operations 
                  across Shaw Wallace, United Spirits, and Diageo, including a 2-year business 
                  transformation project undertaken in collaboration with McKinsey & Company 
                  as a company representative."
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3 pt-4">
                {['Shaw Wallace', 'United Spirits', 'Diageo', 'McKinsey & Company'].map((company) => (
                  <span 
                    key={company}
                    className="px-4 py-2 bg-slate-900 text-white text-sm font-medium"
                  >
                    {company}
                  </span>
                ))}
              </div>
              
              <div className="pt-4">
                <Link to="/about">
                  <Button 
                    data-testid="founder-read-more"
                    variant="outline"
                    className="border-slate-300 text-slate-900 hover:bg-slate-100 rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider"
                  >
                    Read Full Profile
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distillery Gallery */}
      <section data-testid="distillery-gallery" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Our Industry</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
              World-Class Distillery Operations
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                src: "https://images.unsplash.com/photo-1542835497-a6813df96ed9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxkaXN0aWxsZXJ5JTIwY29wcGVyJTIwc3RpbGxzJTIwd2hpc2tleXxlbnwwfHx8fDE3ODA1MDg3NTZ8MA&ixlib=rb-4.1.0&q=85",
                alt: "Copper Pot Stills"
              },
              {
                src: "https://images.unsplash.com/photo-1623051425306-70cfe23fe75e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxkaXN0aWxsZXJ5JTIwY29wcGVyJTIwc3RpbGxzJTIwd2hpc2tleXxlbnwwfHx8fDE3ODA1MDg3NTZ8MA&ixlib=rb-4.1.0&q=85",
                alt: "Distillery Tanks"
              },
              {
                src: "https://images.pexels.com/photos/10040002/pexels-photo-10040002.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                alt: "Traditional Distillery"
              },
              {
                src: "https://images.pexels.com/photos/34635802/pexels-photo-34635802.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                alt: "Modern Distillery Setup"
              },
            ].map((image, index) => (
              <div key={index} className="img-zoom aspect-square">
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section data-testid="value-propositions" className="py-20 lg:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Why Choose Us</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
              Driving Operational Excellence
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: TrendingUp,
                title: 'Performance Transformation',
                description: 'Improve operational efficiency, productivity, and cost competitiveness across your manufacturing operations.',
              },
              {
                icon: Shield,
                title: 'Safe & Compliant',
                description: 'Enable safe, quality-driven, and compliant manufacturing operations in regulated AlcoBev environments.',
              },
              {
                icon: Users,
                title: 'Capability Building',
                description: 'Build leadership capability and strong operating systems for sustainable growth and excellence.',
              },
            ].map((item, index) => (
              <div 
                key={index}
                className="text-center p-8 border border-slate-700 hover:border-blue-500/50 transition-colors duration-300"
              >
                <div className="w-16 h-16 bg-blue-600/20 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-white mb-4">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight mb-6">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve manufacturing excellence 
            and drive sustainable growth for your organization.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="cta-contact"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-none px-10 py-5 text-sm font-bold uppercase tracking-wider btn-shadow"
            >
              Schedule a Consultation
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
